import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';

type QStatus = 'QUARANTINED' | 'RETURNED' | 'DESTROYED' | 'RESTOCKED';

interface QuarantineItem {
  id: string;
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  quantity: number;
  reason: string;
  status: QStatus;
  sourceType: string;
  supplierId: string | null;
  supplierName: string | null;
  supplierReturnId: string | null;
  brfNumber: string | null;
  poNumber: string | null;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
}

const STATUS_TONE: Record<QStatus, 'warning' | 'secondary' | 'destructive' | 'success'> = {
  QUARANTINED: 'warning',
  RETURNED: 'secondary',
  DESTROYED: 'destructive',
  RESTOCKED: 'success',
};

/**
 * SC-10 steps 1–2 — Zone Q.
 *
 * The PURCHASER's review queue: what came in damaged, and the per-item decision. The three
 * outcomes are genuinely different acts, so the screen keeps them apart rather than offering one
 * "resolve" button:
 *
 *   RESTOCK  — it was fine after all. Nothing leaves; the part moves Q → A.
 *   DESTROY  — it leaves for good, and stock drops by that quantity.
 *   RETURN   — it goes back to the supplier, which needs a BRF. Selecting several items of the
 *              SAME supplier and raising one BRF for the lot is the normal case, so the return
 *              action is a multi-select rather than a per-row button.
 */
export default function Quarantine() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();

  const [status, setStatus] = useState<QStatus>('QUARANTINED');
  const [items, setItems] = useState<QuarantineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (s: QStatus = status): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<QuarantineItem[]>(
        `/warehouse/inventory?zone=Q&status=${s}`
      );
      setItems(data);
      setSelected(new Set());
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(status);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = useMemo(() => items.filter((i) => selected.has(i.id)), [items, selected]);

  /**
   * A BRF goes to exactly ONE supplier, so a mixed selection is not a thing we can send. Rather
   * than silently splitting it (two documents the operator did not ask for) the button explains
   * why it is disabled.
   */
  const suppliersInSelection = useMemo(
    () => [...new Set(chosen.map((i) => i.supplierId ?? 'NONE'))],
    [chosen]
  );
  const returnBlocked =
    chosen.length === 0
      ? t('quarantine.selectItems')
      : suppliersInSelection.includes('NONE')
        ? t('quarantine.noSupplierOnSelection')
        : suppliersInSelection.length > 1
          ? t('quarantine.mixedSuppliers')
          : null;

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const decide = async (
    item: QuarantineItem,
    decision: 'DESTROY' | 'RESTOCK' | 'WRITE_OFF'
  ): Promise<void> => {
    // Taking stock off the books needs a reason of at least 10 characters (INV-16); the server
    // refuses a shorter one, so it is asked for here rather than bounced back.
    let note: string | undefined;
    if (decision === 'DESTROY' || decision === 'WRITE_OFF') {
      const answer = window.prompt(t(`quarantine.reasonPrompt.${decision}`, { sku: item.sku }));
      if (answer === null) return;
      note = answer.trim();
      if (note.length < 10) {
        push({ tone: 'warning', message: t('quarantine.reasonTooShort') });
        return;
      }
    }
    setBusy(true);
    try {
      await apiClient.post(`/warehouse/inventory/quarantine/${item.id}/decide`, { decision, note });
      push({ tone: 'success', message: t(`quarantine.decided.${decision}`, { sku: item.sku }) });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const createBrf = async (): Promise<void> => {
    if (returnBlocked || !reason.trim()) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ id: string; brfNumber: string }>(
        '/supplier-returns',
        {
          supplierId: chosen[0].supplierId,
          reason: reason.trim(),
          lines: chosen.map((i) => ({
            productId: i.productId,
            qty: i.quantity,
            reason: i.reason,
            quarantineItemId: i.id,
          })),
        }
      );
      push({ tone: 'success', message: t('quarantine.brfCreated', { number: data.brfNumber }) });
      navigate(`/purchasing/returns?id=${data.id}`);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('quarantine.title')}
        description={t('quarantine.description')}
        actions={
          <Select value={status} onChange={(e) => setStatus(e.target.value as QStatus)}>
            {(['QUARANTINED', 'RETURNED', 'DESTROYED', 'RESTOCKED'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`quarantine.status.${s}`)}
              </option>
            ))}
          </Select>
        }
      />

      {status === 'QUARANTINED' && (
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border bg-muted/30 p-3">
          <div className="min-w-[16rem] flex-1">
            <label className="mb-1 block text-xs font-medium" htmlFor="brf-reason">
              {t('quarantine.returnReason')}
            </label>
            <Input
              id="brf-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('quarantine.returnReasonPlaceholder')}
            />
          </div>
          <Button
            onClick={() => void createBrf()}
            disabled={busy || Boolean(returnBlocked) || !reason.trim()}
            title={returnBlocked ?? undefined}
          >
            {t('quarantine.createBrf', { count: chosen.length })}
          </Button>
          {returnBlocked && chosen.length > 0 && (
            <p className="w-full text-xs text-destructive">{returnBlocked}</p>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              {status === 'QUARANTINED' && <th className="w-10 px-3 py-2" />}
              <th className="px-3 py-2">{t('quarantine.col.product')}</th>
              <th className="px-3 py-2 text-right">{t('quarantine.col.qty')}</th>
              <th className="px-3 py-2">{t('quarantine.col.reason')}</th>
              <th className="px-3 py-2">{t('quarantine.col.origin')}</th>
              <th className="px-3 py-2">{t('quarantine.col.status')}</th>
              {status === 'QUARANTINED' && (
                <th className="px-3 py-2 text-right">{t('quarantine.col.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((i) => (
              <tr key={i.id} className="hover:bg-muted/30">
                {status === 'QUARANTINED' && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={t('quarantine.select', { sku: i.sku })}
                      checked={selected.has(i.id)}
                      onChange={() => toggle(i.id)}
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="font-medium">{i.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {i.sku}
                    {i.brand ? ` · ${i.brand}` : ''}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-medium">{i.quantity}</td>
                <td className="px-3 py-2 text-muted-foreground">{i.reason}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {i.supplierName ?? '—'}
                  {i.poNumber && <div>{i.poNumber}</div>}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={STATUS_TONE[i.status]}>
                    {t(`quarantine.status.${i.status}`)}
                  </Badge>
                  {i.brfNumber && (
                    <div className="mt-1 text-xs text-muted-foreground">{i.brfNumber}</div>
                  )}
                </td>
                {status === 'QUARANTINED' && (
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void decide(i, 'RESTOCK')}
                      >
                        {t('quarantine.restock')}
                      </Button>
                      {/* A part nobody could find (guide E7/E8): the search failed, so it
                          leaves the books. Availability never counted it, so nothing moves there. */}
                      {(i.sourceType === 'PICKING' || i.sourceType === 'HANDOVER') && (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={busy}
                          onClick={() => void decide(i, 'WRITE_OFF')}
                        >
                          {t('quarantine.writeOff')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy}
                        onClick={() => void decide(i, 'DESTROY')}
                      >
                        {t('quarantine.destroy')}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  {t('quarantine.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
